/**
 * Product gallery extras (ARVI 2026-10-18): ‹ › arrows on desktop and a "+" to enlarge the picture.
 * The arrows click the neighbouring thumbnail, so hom-product.js stays the one that switches photos and
 * hom-position.js follows as before. The enlarged view copies what the frame shows right now – catalogue photo,
 * live garment (blank + print) or the sketch – and keeps following it while open; click the big picture to look closer.
 */

const HIRES = 2048;

/** Shopify CDN pictures come in any width – ask for a bigger one in the enlarged view */
function hires(src) {
  if (!src || !/cdn\.shopify\.com|\/cdn\/shop\//.test(src)) return src;
  return src.replace(/([?&])width=\d+/, `$1width=${HIRES}`);
}

const ICON = {
  prev: '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  next: '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  plus: '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"/></svg>',
  close: '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/></svg>',
};

class HomGallery extends HTMLElement {
  /** @type {HTMLElement | null} */ #main = null;
  /** @type {HTMLElement | null} */ #thumbs = null;
  /** @type {HTMLDialogElement | null} */ #dlg = null;
  /** @type {MutationObserver | null} */ #thumbWatch = null;
  /** @type {MutationObserver | null} */ #mainWatch = null;
  #queued = 0;

  connectedCallback() {
    this.#main = this.querySelector('.main');
    this.#thumbs = this.querySelector('[data-thumbs]');
    if (!this.#main || this.#main.querySelector('.gal-zoom')) return;

    this.#main.insertAdjacentHTML(
      'beforeend',
      `<button type="button" class="gal-nav prev" data-gal-step="-1" aria-label="Previous photo" hidden>${ICON.prev}</button>` +
        `<button type="button" class="gal-nav next" data-gal-step="1" aria-label="Next photo" hidden>${ICON.next}</button>` +
        `<button type="button" class="gal-zoom" data-gal-zoom aria-label="Enlarge photo">${ICON.plus}</button>`,
    );
    this.#main.addEventListener('click', this.#onMainClick);

    // the thumbnails are drawn by hom-product.js – the arrows only make sense with two pictures or more
    this.#syncNav();
    if (this.#thumbs) {
      this.#thumbWatch = new MutationObserver(() => this.#syncNav());
      this.#thumbWatch.observe(this.#thumbs, { childList: true });
    }
  }

  disconnectedCallback() {
    this.#thumbWatch?.disconnect();
    this.#mainWatch?.disconnect();
    this.#main?.removeEventListener('click', this.#onMainClick);
  }

  #list() {
    return /** @type {HTMLButtonElement[]} */ ([...(this.#thumbs?.querySelectorAll('[data-thumb]') || [])]);
  }

  #current() {
    const list = this.#list();
    return Math.max(0, list.findIndex((b) => b.getAttribute('aria-pressed') === 'true'));
  }

  #syncNav() {
    const many = this.#list().length > 1;
    for (const b of this.#main?.querySelectorAll('.gal-nav') || []) /** @type {HTMLElement} */ (b).hidden = !many;
    if (this.#dlg) for (const b of this.#dlg.querySelectorAll('.lb-nav')) /** @type {HTMLElement} */ (b).hidden = !many;
  }

  /** @param {number} d */
  #step(d) {
    const list = this.#list();
    if (list.length < 2) return;
    list[(this.#current() + d + list.length) % list.length].click();
  }

  /** @param {MouseEvent} e */
  #onMainClick = (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    const nav = t.closest('[data-gal-step]');
    if (nav) return this.#step(Number(nav.getAttribute('data-gal-step')));
    if (t.closest('[data-gal-zoom]')) return this.#open();
    // a click on the picture itself enlarges it; the studio buttons and the view toggle keep their own job
    if (t.closest('button, a, input, label, [role="group"]')) return;
    this.#open();
  };

  // ---------- enlarged view ----------

  #build() {
    const d = document.createElement('dialog');
    d.className = 'hom-lb';
    d.setAttribute('aria-label', 'Enlarged photo');
    d.innerHTML =
      `<button type="button" class="lb-close" data-lb-close aria-label="Close">${ICON.close}</button>` +
      '<div class="lb-body">' +
      `<button type="button" class="lb-nav prev" data-lb-step="-1" aria-label="Previous photo">${ICON.prev}</button>` +
      '<div class="lb-frame" data-lb-frame><div class="lb-stage" data-lb-stage></div></div>' +
      `<button type="button" class="lb-nav next" data-lb-step="1" aria-label="Next photo">${ICON.next}</button>` +
      '<p class="lb-cap"><b data-lb-count></b> <span data-lb-label></span><small data-lb-hint>Click the picture to look closer</small></p>' +
      '</div>';
    d.addEventListener('click', this.#onDlgClick);
    d.addEventListener('keydown', this.#onDlgKey);
    d.addEventListener('close', this.#onClose);
    d.querySelector('[data-lb-frame]')?.addEventListener('pointermove', this.#onPan);
    // inside the gallery (so the colours of the page apply); showModal() lifts it above everything
    this.append(d);
    return d;
  }

  #open() {
    if (!this.#dlg) {
      this.#dlg = this.#build();
      this.#syncNav();
    }
    this.#zoom(false);
    this.#render();
    this.#dlg.showModal();
    document.documentElement.classList.add('hom-lb-open');
    // arrows, colour or a new sketch change the frame underneath – the big picture follows
    this.#mainWatch = new MutationObserver(() => {
      cancelAnimationFrame(this.#queued);
      this.#queued = requestAnimationFrame(() => this.#render());
    });
    if (this.#main) this.#mainWatch.observe(this.#main, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'style', 'hidden'] });
  }

  #onClose = () => {
    this.#mainWatch?.disconnect();
    this.#mainWatch = null;
    cancelAnimationFrame(this.#queued);
    document.documentElement.classList.remove('hom-lb-open');
  };

  /** copy the visible layers of the frame (photo, live garment, sketch overlay) into the big frame */
  #render() {
    const d = this.#dlg;
    const main = this.#main;
    if (!d || !main) return;
    const frame = /** @type {HTMLElement} */ (d.querySelector('[data-lb-frame]'));
    const stage = /** @type {HTMLElement} */ (d.querySelector('[data-lb-stage]'));
    // same proportions as the page frame, so the print lands on the same spot of the garment
    if (main.clientWidth && main.clientHeight) frame.style.setProperty('--ar', String(main.clientWidth / main.clientHeight));

    const layers = [];
    const photo = main.querySelector('[data-main-img]');
    if (photo instanceof HTMLImageElement) layers.push(this.#copy(photo, 'lb-img'));
    const live = main.querySelector('[data-live]');
    if (live instanceof HTMLElement && !live.hidden) {
      const c = /** @type {HTMLElement} */ (live.cloneNode(true));
      c.className = 'lb-live';
      c.removeAttribute('data-live');
      const blank = c.querySelector('[data-live-blank]');
      if (blank instanceof HTMLImageElement) this.#tidy(blank, 'lb-img');
      const art = c.querySelector('[data-live-art]');
      if (art instanceof HTMLImageElement) this.#tidy(art, 'lb-art');
      layers.push(c);
    }
    const overlay = main.querySelector('[data-sk-overlay]');
    if (overlay instanceof HTMLImageElement && !overlay.hidden && overlay.getAttribute('src')) layers.push(this.#copy(overlay, 'lb-img'));
    stage.replaceChildren(...layers);

    const list = this.#list();
    const i = this.#current();
    const count = d.querySelector('[data-lb-count]');
    if (count) count.textContent = list.length > 1 ? `${i + 1} / ${list.length}` : '';
    const label = d.querySelector('[data-lb-label]');
    if (label) label.textContent = list[i]?.getAttribute('aria-label') || '';
  }

  /** @param {HTMLImageElement} img @param {string} cls */
  #copy(img, cls) {
    const c = /** @type {HTMLImageElement} */ (img.cloneNode(false));
    this.#tidy(c, cls);
    return c;
  }

  /** a copy must not answer the page scripts' selectors (data-*), and wants the sharp picture right away */
  #tidy(img, cls) {
    for (const a of [...img.attributes]) if (a.name.startsWith('data-') || a.name === 'id') img.removeAttribute(a.name);
    img.removeAttribute('loading');
    img.removeAttribute('fetchpriority');
    img.className = cls;
    const src = img.getAttribute('src');
    if (src) {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = hires(src);
    }
  }

  /** @param {boolean} on @param {PointerEvent | MouseEvent} [e] */
  #zoom(on, e) {
    const frame = this.#dlg?.querySelector('[data-lb-frame]');
    if (!(frame instanceof HTMLElement)) return;
    if (on && e) this.#aim(frame, e);
    frame.classList.toggle('zoomed', on);
    const hint = this.#dlg?.querySelector('[data-lb-hint]');
    const verb = matchMedia('(hover: none)').matches ? 'Tap' : 'Click';
    if (hint) hint.textContent = on ? `${verb} again to see it whole` : `${verb} the picture to look closer`;
  }

  /** @param {HTMLElement} frame @param {PointerEvent | MouseEvent} e */
  #aim(frame, e) {
    const r = frame.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
    frame.style.setProperty('--zx', `${x}%`);
    frame.style.setProperty('--zy', `${y}%`);
  }

  /** @param {PointerEvent} e */
  #onPan = (e) => {
    const frame = e.currentTarget;
    if (e.pointerType === 'mouse' && frame instanceof HTMLElement && frame.classList.contains('zoomed')) this.#aim(frame, e);
  };

  /** @param {MouseEvent} e */
  #onDlgClick = (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t || !this.#dlg) return;
    const nav = t.closest('[data-lb-step]');
    if (nav) {
      this.#zoom(false);
      return this.#step(Number(nav.getAttribute('data-lb-step')));
    }
    if (t.closest('[data-lb-close]')) return this.#dlg.close();
    const frame = t.closest('[data-lb-frame]');
    if (frame) return this.#zoom(!frame.classList.contains('zoomed'), e);
    // the empty space around the picture closes it
    if (!t.closest('.lb-cap')) this.#dlg.close();
  };

  /** @param {KeyboardEvent} e */
  #onDlgKey = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    this.#zoom(false);
    this.#step(e.key === 'ArrowLeft' ? -1 : 1);
  };
}

if (!customElements.get('hom-gallery')) customElements.define('hom-gallery', HomGallery);
